import { type FC, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { getImage } from '@common/lib/images';
import { getText } from '@common/lib/text';
import { type BuildKindType } from '@common/types/common';
import { type RequirementsItemProps } from '@common/types/components';
import { type FactoryTypeText } from '@common/types/factory';
import { type ResourceTypeText } from '@common/types/resource';
import { WorkshopPreview } from '@components/Goods/WorkshopPreview/WorkshopPreview';
import { useHighlightComponentRect } from '@components/Intro/hooks/highlight-component-rect.ts';
import { RequirementsItem } from '@components/RequirementsItem/RequirementsItem';
import { ResourceTitle } from '@components/ResourceTitle/ResourceTitle';
import { H1, H3, TextMain } from '@components/Typography/Typography';
import { PageBackground } from '@components/ui/Backgrounds/PageBackground/PageBackground';
import { MainButton } from '@components/ui/Button/Button';
import { MineImage } from '@components/ui/MineImage/MineImage';
import cn from 'classnames';

import css from './styles.module.scss';

interface FirstBuildProps {
  kind: BuildKindType;
  name: string;
  requirementsList: RequirementsItemProps[];
  isComplete: boolean;
  onBuild: () => void;
  isLoading?: boolean;
  isFirstBuild?: boolean;
}

const FirstBuild: FC<FirstBuildProps> = memo(
  ({
    kind,
    isFirstBuild,
    name,
    requirementsList,
    isComplete,
    isLoading,
    onBuild,
  }) => {
    const { t } = useTranslation();

    const stepId = kind === 'quarry' ? 2 : kind === 'factory' ? 9 : 19;

    const ref = useHighlightComponentRect<HTMLButtonElement>(stepId);

    const background = getImage({ name: name, field: 'background' });
    const title = getText({ name: name, kind: 'build', t });
    const description =
      kind === 'quarry'
        ? 'Build your first resource facility'
        : kind === 'factory'
          ? 'Build your first material processing plant'
          : 'Build your first goods production factory';

    return (
      <main className="h-full w-full">
        <PageBackground
          className={cn(
            'absolute top-0 left-0 z-[-1] dark-gradient',
            isFirstBuild ? 'h-[81.87vh]' : 'h-[61.56vh]',
            isFirstBuild && kind === 'factory' && '!h-[61.56vh]',
          )}
          backgroundImage={{
            png: background?.img ?? '',
            webp: background?.webp ?? '',
          }}
        />
        <div className={css.buildContent}>
          {!isFirstBuild && (
            <ResourceTitle className="sm-h:mb-2 mb-[3.25rem]">
              {title}
            </ResourceTitle>
          )}
          <div
            className={cn(
              'flex-1 min-h-0 flex justify-center w-full',
              isFirstBuild ? 'items-end' : 'items-center',
            )}
          >
            {kind === 'quarry' && (
              <MineImage
                className="sm-h:mb-2 mb-12 w-full min-h-0"
                resourceName={name as ResourceTypeText}
              />
            )}
          </div>
          {kind === 'workshop' && (
            <WorkshopPreview name={name as FactoryTypeText} />
          )}
          {isFirstBuild && (
            <div className="w-full flex flex-col items-center mb-6">
              <H1 weight="black" className="mb-2">
                {title}
              </H1>
              <TextMain weight="regular" className="text-white/60 leading-1.2">
                {t(description)}
              </TextMain>
            </div>
          )}
          <div className="w-full flex flex-col">
            <div className="w-full flex flex-col items-center mb-4">
              {!isFirstBuild && (
                <H3 weight="bold" className="text-white mb-4">
                  {t('Requirements')}
                </H3>
              )}
              <div className="w-full flex flex-col gap-1">
                {(requirementsList || [{ type: 'cash', value: 1000 }]).map(
                  (props, index) => (
                    <RequirementsItem key={index} {...props} />
                  ),
                )}
              </div>
            </div>
            <MainButton
              ref={ref}
              isLoading={isLoading}
              onClick={onBuild}
              disabled={!isComplete || isLoading}
              id={
                kind === 'quarry'
                  ? 'intro-step-2'
                  : kind === 'factory'
                    ? 'intro-step-9'
                    : kind === 'workshop'
                      ? 'intro-step-19'
                      : undefined
              }
              inactive={!isComplete}
              text={t('Build')}
            ></MainButton>
          </div>
        </div>
      </main>
    );
  },
);

export default FirstBuild;
